import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { useMemo } from 'react'
import { REMINDERS_QUERY_KEY, type ReminderApiItem, mapApiItemToReminderItem } from './use-reminders'
import { useRemindersData } from '../reminders-context'

interface UseReminderOptions {
  reminderId: string | undefined
}

/** One reminder as the API returns it, with what only a single reminder carries; null once it's gone. */
export async function fetchReminder(token: string, reminderId: string): Promise<ReminderApiItem | null> {
  const response = await fetch(getChatApiRoute(`api/v1/reminders/${reminderId}`), {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (response.status === 404) return null
  if (!response.ok) throw new Error('Failed to fetch reminder')
  const json = await response.json()
  return json.data as ReminderApiItem
}

/** The query a single reminder is cached under, shared by everything that fetches one. */
export const reminderQueryKey = (reminderId: string | undefined) => [REMINDERS_QUERY_KEY, reminderId]

export function useReminder({ reminderId }: UseReminderOptions) {
  const { getAuthToken } = useAuth()
  // Shared instance from RemindersDataProvider — mounting useReminders here
  // would spin up a duplicate query observer per open reminder drawer.
  const { items, isLoading: isLoadingAll } = useRemindersData()

  const cachedReminder = useMemo(() => items.find((i) => i.id === reminderId) ?? null, [items, reminderId])

  const {
    data: fetchedApiItem,
    isLoading: isFetching,
    error,
  } = useQuery({
    queryKey: reminderQueryKey(reminderId),
    queryFn: async () => fetchReminder(await getAuthToken(), reminderId!),
    enabled: !!reminderId && !cachedReminder && !isLoadingAll,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

  const fetchedReminder = useMemo(
    () => (fetchedApiItem ? mapApiItemToReminderItem(fetchedApiItem) : null),
    [fetchedApiItem],
  )

  return {
    reminder: cachedReminder ?? fetchedReminder ?? null,
    isLoading: isLoadingAll || (isFetching && !cachedReminder),
    error,
  }
}
